import Link from "next/link";
import SidebarItem from "./SidebarItem";
import { useState, useEffect, lazy, Suspense } from "react";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
const LazyResourceMonitor = lazy(() => import("./SidebarResourceMonitor"));
const ReferralLink = lazy(() => import("./ReferralLink"));

const Sidebar = ({ items, onPanelToggle, t }) => {
  const [openPanel, setOpenPanel] = useState(null);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isMobileViewport = window.matchMedia("(max-width: 767.98px)").matches;
    if (isMobileViewport) {
      setIsSidebarCollapsed(true);
      setIsPanelCollapsed(true);
    }
  }, []);

  // Calculate the total width of the sidebar
  useEffect(() => {
    const mainSidebarWidth = isSidebarCollapsed ? 60 : 220;
    const secondarySidebarWidth = openPanel && !isPanelCollapsed ? 220 : 0;
    const totalWidth = mainSidebarWidth + secondarySidebarWidth;

    // Call the callback to update parent component
    if (onPanelToggle) {
      onPanelToggle(totalWidth);
    }
  }, [openPanel, isPanelCollapsed, isSidebarCollapsed, onPanelToggle]);

  const handleItemClick = (itemId, hasChildren) => {
    if (hasChildren) {
      setOpenPanel((prev) => {
        const newPanel = prev === itemId ? null : itemId;
        // If opening a new panel, make sure it's not collapsed
        if (newPanel) {
          setIsPanelCollapsed(false);
        }
        return newPanel;
      });
    } else {
      setOpenPanel(null);
    }
  };

  const togglePanelCollapse = () => {
    setIsPanelCollapsed((prev) => !prev);
  };

  const toggleSidebarCollapse = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const activePanel = items.find((i) => i.id === openPanel);

  return (
    <div className="d-flex h-100 position-relative">
      {/* Main sidebar */}
      <div
        className="d-flex flex-column pt-3 scrollbar position-relative bg-bota-purple"
        style={{
          width: isSidebarCollapsed ? "60px" : "220px",
          minWidth: isSidebarCollapsed ? "60px" : "220px",
          maxWidth: isSidebarCollapsed ? "60px" : "220px",
          flexShrink: 0,
          overflowY: "auto",
          overflowX: "hidden",
          transition:
            "width 0.3s ease, min-width 0.3s ease, max-width 0.3s ease",
        }}
      >
        {/* Toggle button at the edge */}
        <button
          onClick={toggleSidebarCollapse}
          className="btn d-flex align-items-center justify-content-center tw-bg-botatende-gradient2"
          style={{
            position: "absolute",
            top: "10px",
            right: "-11px",
            transform: "translateX(-50%)",
            width: "24px",
            height: "24px",
            // backgroundColor: "var(--bs-bota-strong-cyan-lime-green)",
            color: "white",
            border: "2px solid white",
            borderRadius: "50%",
            padding: "0",
            zIndex: 1001,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          {isSidebarCollapsed ? (
            <KeyboardDoubleArrowRightIcon
              style={{ color: "white", fontSize: "16px" }}
            />
          ) : (
            <KeyboardDoubleArrowLeftIcon
              style={{ color: "white", fontSize: "16px" }}
            />
          )}
        </button>

        {/* Sidebar items */}
        {items.map((item) => {
          const isParentActive =
            item.isRouteActive ||
            (item.children?.some((child) => child.isRouteActive) ?? false);

          return (
            <SidebarItem
              key={item.id}
              item={item}
              isActive={isParentActive}
              isCollapsed={isSidebarCollapsed}
              onClick={() => handleItemClick(item.id, !!item.children)}
            />
          );
        })}

        {/* Resource Monitor */}
        <Suspense
          fallback={
            <div className="tw-p-4 tw-mt-auto">
              <div className="tw-space-y-4 tw-p-4 tw-rounded-xl tw-bg-gray-800">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="tw-animate-pulse">
                    <div className="tw-h-4 tw-bg-gray-700/50 tw-rounded tw-mb-2"></div>
                    <div className="tw-h-2 tw-bg-gray-700/50 tw-rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          }
        >
          <LazyResourceMonitor isCollapsed={isSidebarCollapsed} t={t} />
        </Suspense>

        {/* Referral Links */}
        <Suspense
          fallback={
            <div className="tw-p-4 tw-mt-4">
              <div className="tw-space-y-4 tw-p-4 tw-rounded-xl tw-bg-gray-800">
                <div className="tw-animate-pulse">
                  <div className="tw-h-4 tw-bg-gray-700/50 tw-rounded tw-mb-2"></div>
                  <div className="tw-h-2 tw-bg-gray-700/50 tw-rounded"></div>
                </div>
              </div>
            </div>
          }
        >
          <ReferralLink isCollapsed={isSidebarCollapsed} t={t} />
        </Suspense>
      </div>

      {/* Secondary sidebar */}
      {activePanel && (
        <div
          className="d-flex flex-column border-end position-relative"
          style={{
            width: isPanelCollapsed ? "0px" : "220px",
            backgroundColor: "var(--bs-white)",
            color: "var(--bs-bota-light-blue)",
            flexShrink: 0,
            transition: "width 0.3s ease",
            overflow: "hidden",
          }}
        >
          <div
            className="fw-bold px-3 py-2 border-bottom"
            style={{
              color: "var(--bs-white)",
              backgroundColor: "var(--bs-bota-light-blue)",
              minWidth: "220px",
            }}
          >
            {activePanel.label}
          </div>
          <div
            className="list-group list-group-flush scrollbar secondary-sidebar"
            style={{
              minWidth: "220px",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {activePanel.children?.map((child) => (
              <Link
                key={child.id}
                href={child.url || "#"}
                className={`list-group-item
                   list-group-item-action ${
                     child.isRouteActive ? "active" : ""
                   }`}
              >
                {child.icon && (
                  <span className="me-2 icon-wrapper">{child.icon}</span>
                )}
                {child.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Floating toggle button for secondary panel */}
      {activePanel?.children && !(isSidebarCollapsed && isPanelCollapsed) && (
        <button
          onClick={togglePanelCollapse}
          className="btn rounded-circle d-flex align-items-center justify-content-center tw-bg-botatende-gradient1"
          style={{
            position: "absolute",
            right: isPanelCollapsed
              ? "-160px"
              : isSidebarCollapsed
                ? "-220px"
                : "-380px",
            top: "50%",
            transform: "translateY(-50%)",
            width: "40px",
            height: "40px",
            // backgroundColor: "var(--bs-bota-strong-cyan-lime-green)",
            color: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            transition: "right 0.3s ease",
            zIndex: 1000,
            border: "2px solid white",
          }}
        >
          {isPanelCollapsed ? (
            <KeyboardDoubleArrowRightIcon style={{ color: "white" }} />
          ) : (
            <KeyboardDoubleArrowLeftIcon style={{ color: "white" }} />
          )}
        </button>
      )}
    </div>
  );
};

export default Sidebar;
